import React, { createRef, PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import scaleSize from '../utils/scale-size';
import { FocusableParent } from './navigation';
import ListItem, { LIST_ITEM_MARGIN } from './ListItem';
import config, { LIST_ITEMS_SIZE, LIST_TYPES } from '../config';
import { CONTENT_TYPES } from '../constants/content-types';

const ListWrapper = styled(FocusableParent)`
  height: ${({ listHeight }) => scaleSize(listHeight)}px;
  margin-left: ${({ listType }) => (listType === LIST_TYPES.SEARCH ? `${scaleSize(140)}px` : 0)};
  position: relative;
  white-space: nowrap;
  //transform: translateY(${({ moveDown }) => (moveDown ? scaleSize(48) : 0)}px);
  transform: translate3d(0, ${({ moveDown }) => (moveDown ? scaleSize(48) : 0)}px, 0);

  will-change: transform;
`;

const ListTitle = styled.div`
  height: ${({ listType }) => (listType === LIST_TYPES.SEARCH ? 'auto' : `${scaleSize(200)}px`)};
  box-sizing: border-box;
  padding-bottom: ${scaleSize(35)}px;
  padding-top: ${({ listType }) => (listType === LIST_TYPES.SEARCH ? `${scaleSize(35)}px` : `${scaleSize(117)}px`)};
  font-weight: 500;
  font-size: ${scaleSize(40)}px;
  line-height: 1.2;
  letter-spacing: normal;
  color: ${({ theme }) => theme.defaultColor};
  font-family: RobotoMedium, sans-serif;
`;

const BannerListTitle = styled.div`
  height: ${scaleSize(175)}px;
  margin-top: ${scaleSize(145)}px;
`;

const ListContents = styled.div.attrs(({ listOffset }) => ({
  style: { transform: `translate3d(-${scaleSize(listOffset)}px, 0, 0)` },
}))`
  transition: ${({ noAnimation }) =>
    config.withAnimation && !noAnimation ? `transform ${config.mainTransitionDuration}` : 'none'};
`;

class List extends PureComponent {
  state = {
    focusedItemIndex: 0,
  };

  listRef = createRef();
  offsetTop = 0;

  componentDidMount() {
    this.listRef.current && (this.offsetTop = this.listRef.current.offsetTop);
  }

  onListItemFocus = (item, itemIndex, itemId) => {
    const { onListItemFocus, listIndex } = this.props;

    this.setState({ focusedItemIndex: itemIndex });

    onListItemFocus && onListItemFocus({ listIndex, itemId, itemIndex }, this.offsetTop);
  };

  onItemEnter = (item, payload = {}) => {
    const { onListItemEnter, listId } = this.props;
    const extendedPayload = { ...payload, listId };

    onListItemEnter && onListItemEnter(item, extendedPayload);
  };

  calculateListOffset() {
    const { focusedItemIndex } = this.state;
    const { listType, items } = this.props;

    const itemWidth = LIST_ITEMS_SIZE[listType].width + LIST_ITEM_MARGIN;
    const listOffset = LIST_ITEMS_SIZE[listType].offset;

    if (listOffset === 0) {
      return focusedItemIndex * itemWidth;
    }

    if (items.length - focusedItemIndex < listOffset) {
      return (items.length - listOffset) * itemWidth;
    }

    if (focusedItemIndex > 1) {
      return (focusedItemIndex - 1) * itemWidth;
    }

    return 0;
  }

  getItemId(item) {
    const { listId } = this.props;
    return `list-${listId}-item-${item.targetId || item.id}`;
  }

  renderListTitle() {
    const { listType, title } = this.props;

    if (listType === LIST_TYPES.BANNER) {
      return <BannerListTitle />;
    }

    if (title) {
      return <ListTitle listType={listType}>{title}</ListTitle>;
    }

    return null;
  }
  // Todo: переделать. каждый itemType должен быть отдельным компонентом
  // с прописанным листом разрешенных props
  renderListItem() {
    const { listId, listType, itemType, items, focusedListIndex, listIndex } = this.props;

    return items.map((item, index) => {
      const itemId = this.getItemId(item);
      const showProgress = item.type === CONTENT_TYPES.VOD_BOOKMARK;
      return (
        <ListItem
          onFocus={(item, index) => this.onListItemFocus(item, index, itemId)}
          item={{ ...item, type: item.type || itemType }}
          itemIndex={index}
          onEnter={this.onItemEnter}
          hideTitle={focusedListIndex > listIndex}
          className={`list-${listId}-item-${index}`}
          key={`${listId}-${item.targetId || item.id}`}
          id={itemId}
          payload={{ itemId }}
          showProgress={showProgress}
          itemSize={LIST_ITEMS_SIZE[listType]}
        />
      );
    });
  }

  render() {
    const {
      defaultSection,
      sectionName,
      listId,
      config,
      listType,
      focusedListIndex,
      listIndex,
      noAnimation,
      extraPadding,
      onWheel,
    } = this.props;

    return (
      <ListWrapper
        moveDown={listIndex - focusedListIndex === 1}
        defaultSection={defaultSection}
        id={`list-${listId}`}
        config={{
          ...config,
          defaultElement: `.list-${listId}-item-0`,
          enterTo: 'last-focused',
        }}
        sectionName={sectionName}
        ref={this.listRef}
        listType={listType}
        listHeight={LIST_ITEMS_SIZE[listType].height + extraPadding}
      >
        {this.renderListTitle()}
        <ListContents onWheel={onWheel} noAnimation={noAnimation} listOffset={this.calculateListOffset()}>
          {this.renderListItem()}
        </ListContents>
      </ListWrapper>
    );
  }
}

List.propTypes = {
  listId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  items: PropTypes.array.isRequired,
  onListItemEnter: PropTypes.func,
  onListItemFocus: PropTypes.func,
  sectionName: PropTypes.string,
  listIndex: PropTypes.number,
  focusedListIndex: PropTypes.number,
  defaultSection: PropTypes.bool,
  noAnimation: PropTypes.bool,
  extraPadding: PropTypes.number,
  config: PropTypes.object,
  listType: PropTypes.string,
};

List.defaultProps = {
  listType: 'LARGE',
  noAnimation: false,
  // Для совместимости. нужен рефактор
  extraPadding: 200,
};

export default List;
